import * as React from 'react';
import PopupWrapper from '../../Popup/PopupWrapper';
import PopupHeader from '../../Popup/PopupHeader';
import PopupBody from '../../Popup/PopupBody';
import { CodeIconSvg, EyeIconSvg, SplitIconSvg } from '../../../pages/project/AlanButtonPopupSettingsForm/alanPopupEditorIcons';
import { useCallback, useEffect, useState } from 'react';
import { TransformHtmlViewer } from '../../../pages/project/TransformMode/TransformHtmlViewer';
import { Controlled as ReactCodeMirror } from 'react-codemirror2';
import TextFieldWithJsonWrapper from '../../TextFieldWithJsonWrapper/TextFieldWithJsonWrapper';
import { isValidJSON } from '../../../../services/mini/isValidJSON';
import { TransformMarkdownViewer } from '../../../pages/project/TransformMode/TransformMarkdownViewer';
import { getMarkdownWithTransformFixes } from '../../../../services/mini/getMarkdownWithTransformFixes';
import '../../../../services/cm/transformsKeywords';
import { HTML_CM_TRANSFORM_MODE, JS_CM_TRANSFORM_MODE, TEXT_CM_TRANSFORM_MODE } from '../../../pages/project/TransformMode/cm_transform_modes';
import RunScriptButton from '../../../pages/project/TransformMode/RunScriptButton/RunScriptButton';
import { getValidationMsgForJSONFromTransform, isTransformValueValidJSON } from '../../../pages/project/TransformMode/cleanupTransformsJSON';
import { useClosePopup } from '../../../../hooks/useClosePopup';
import isLocalStorageAvailable from '../../../../services/mini/isLocalStorageAvailable';
import { TransformViewerMode } from '../../../../models/TransformViewerMode';
import OpenJsFiddleButton from '../../../pages/project/TransformMode/OpenJsFiddleButton/OpenJsFiddleButton';

const options = {
    lineWrapping: false,
    mode: TEXT_CM_TRANSFORM_MODE,
    showCursorWhenSelecting: true,
};

function formatJsonInText(text) {
    return text.replace(/({.*?})/gs, (match) => {
        try {
            const parsedJson = JSON.parse(match);
            return JSON.stringify(parsedJson, null, 2);
        } catch (e) {
            return match;
        }
    });
}

function setGraphRendererDetailsPanelsState(mode: string, projectId: string) {
    if (isLocalStorageAvailable()) {
        localStorage.setItem(`alan_graph-details-panel-state-${projectId}`, mode);
    }
}

function getGraphRendererDetailsPanelsState(projectId): TransformViewerMode {
    if (isLocalStorageAvailable()) {
        return localStorage.getItem(`alan_graph-details-panel-state-${projectId}`) as TransformViewerMode || 'editor';
    }
    return 'editor';
}

function GraphRendererDetailsPopup({ format, initValue, onClose, title, environment, projectId }) {

    const isValid = isTransformValueValidJSON(format, initValue || '');
    const jsonValidationMsg = getValidationMsgForJSONFromTransform(format, initValue || '');

    const [viewer, setViewer] = useState<TransformViewerMode>(getGraphRendererDetailsPanelsState(projectId));
    const [value, setValue] = useState(initValue);
    const editable = false;
    const canBeWrapped = false; //viewer === 'editor' ? (initValue || '')?.split(/\n/).length < 19 : false;

    useEffect(() => {
        if (format === 'json') {
            try {
                setValue(JSON.stringify(JSON.parse(initValue), null, 4));
            } catch (error) {
                try {
                    setValue(formatJsonInText(initValue));
                } catch (error2) {
                    setValue(initValue);
                }
            }
        } else {
            setValue(initValue);
        }

    }, [format, initValue, isValid, value]);

    useClosePopup(onClose);

    return <PopupWrapper className="graph-renderer-details-popup ">
        <PopupHeader
            title={title || "Details"}
            onCancel={onClose}>
            <React.Fragment>
                {/* <div className="graph-renderer-details-popup__type">(type: {format})</div> */}
                <div className="graph-renderer-details-popup__header-ctrls">

                    {(format === 'markdown') &&
                        <OpenJsFiddleButton value={value} width={30} height={30} />
                    }

                    {(format === 'markdown' || format === 'html') &&
                        <div className={`graph-renderer-details-popup__header-modes`}>
                            <div
                                title="Code"
                                className={`graph-renderer-details-popup__header-mode-btn ${viewer === 'editor' ? 'selected' : ''}`}
                                onClick={() => {
                                    setViewer('editor');
                                    setGraphRendererDetailsPanelsState('editor', projectId);
                                }}>
                                <CodeIconSvg />
                            </div>
                            <div
                                title="Split"
                                className={`graph-renderer-details-popup__header-mode-btn ${viewer === 'both' ? 'selected' : ''}`}
                                onClick={() => {
                                    setViewer('both');
                                    setGraphRendererDetailsPanelsState('both', projectId);
                                }}>
                                <SplitIconSvg />
                            </div>
                            <div
                                title="Preview"
                                className={`graph-renderer-details-popup__header-mode-btn ${viewer === 'preview' ? 'selected' : ''}`}
                                onClick={() => {
                                    setViewer('preview');
                                    setGraphRendererDetailsPanelsState('preview', projectId);
                                }}>
                                <EyeIconSvg />
                            </div>
                        </div>}


                    {(format === 'javascript') && <RunScriptButton
                        text={initValue || ''}
                        className="transform-row-popup-column__run-script-btn graph-renderer-details-popup__run-script-btn"
                        transformName={title}
                        environment={environment} />}
                </div>
            </React.Fragment>
        </PopupHeader>
        <PopupBody>

            {format === 'text' &&
                <>
                    <div className="graph-renderer-details-popup__one-col-editor  ">
                        <ReactCodeMirror
                            autoCursor={true}
                            value={value}
                            options={{ ...options, readOnly: !editable, lineWrapping: canBeWrapped }}
                            onBeforeChange={(editor, data, value) => {
                            }}
                        />
                    </div>
                </>
            }
            {format === 'json' &&
                <>
                    <div className={`graph-renderer-details-popup__one-col-editor ${!isValid ? 'invalid' : ''}`}>
                        <TextFieldWithJsonWrapper
                            value={value}
                            readOnly={true}
                            validationMessage={'JSON is not valid'}
                            noAutoFocus={true}
                            noBorder={true}
                            mode={JS_CM_TRANSFORM_MODE}
                            skipValidation={true}
                        />
                        {!isValid && <i title={jsonValidationMsg} className="icon graph-renderer-details-popup__err-icon icon-alan-info-sign" />}
                    </div>
                </>
            }
            {format === 'markdown' &&
                <>
                    <div className={`graph-renderer-details-popup__two-cols-preview ${viewer}`}>
                        {viewer !== 'preview' && <div className="graph-renderer-details-popup__two-cols-editor">
                            <ReactCodeMirror
                                autoCursor={true}
                                value={value}
                                options={{ ...options, readOnly: !editable, lineWrapping: canBeWrapped }}
                                onBeforeChange={(editor, data, value) => {
                                }}
                            />
                        </div>}

                        {viewer !== 'editor' && <div className="graph-renderer-details-popup__markdown-preview with-markdown studio-markdown">
                            <TransformMarkdownViewer value={getMarkdownWithTransformFixes(value)} />
                        </div>}
                    </div>
                </>

            }

            {format === 'html' &&
                <>
                    <div className={`graph-renderer-details-popup__two-cols-preview ${viewer}`}>
                        {viewer !== 'preview' && <div className="graph-renderer-details-popup__two-cols-editor  ">
                            <ReactCodeMirror
                                autoCursor={true}
                                value={value}
                                options={{ ...options, readOnly: !editable, mode: HTML_CM_TRANSFORM_MODE, }}
                                onBeforeChange={(editor, data, value) => {
                                }}
                            />
                        </div>}
                        {viewer !== 'editor' && <div className="graph-renderer-details-popup__markdown-preview ">
                            <TransformHtmlViewer value={value} />
                        </div>}
                    </div>
                </>
            }

            {format === 'javascript' &&
                <>
                    <div className="graph-renderer-details-popup__one-col-editor  ">
                        <ReactCodeMirror
                            autoCursor={true}
                            value={value}
                            options={{ ...options, readOnly: !editable, lineWrapping: canBeWrapped, mode: JS_CM_TRANSFORM_MODE, matchBrackets: true, }}
                            onBeforeChange={(editor, data, value) => {
                            }}
                        />
                    </div>
                </>
            }


        </PopupBody>
    </PopupWrapper>;
}

export default GraphRendererDetailsPopup;